/**
 * Convert a string to sentence case
 * @param word string to be converted
 * @returns string string in sentence case
 */
export function toSentenceCase(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

/**
 * Convert a sentence to title case
 * @param sentence string to be converted
 * @returns string string in title case
 */
export function toTitleCase(sentence: string): string {
  return sentence
    .split(" ")
    .map((word) => toSentenceCase(word))
    .join(" ");
}

/**
 * Convert a string to kebab case
 * @param sentence string to be converted
 * @returns string string in kebab case
 */
export function toKebabCase(sentence: string): string {
  return sentence
    .split(" ")
    .map((word) => word.toLowerCase())
    .join("-");
}

/**
 * Check if a string is a valid JSON
 * @param {string} str string to be checked
 * @returns {boolean} true if string is a valid JSON
 */
export function isJson(str: string): boolean {
  try {
    JSON.parse(str);
    return true;
  } catch (e) {
    if (e instanceof SyntaxError) {
      return false;
    }
    throw e; // Re-throw if it's an unexpected error
  }
}

/**
 * Generates a URL-friendly slug from a string.
 *
 * @param {string} text The input string to convert.
 * @returns {string} The generated slug.
 */
export const generateSlug = (text: string): string => {
  if (text.trim() === "") {
    // Check for empty string *after* trimming
    throw new Error("Input string cannot be empty or contain only whitespace.");
  }

  let slug = text
    .toLowerCase()
    .trim()
    .normalize("NFD") // Decompose Unicode characters
    .replace(/[\u0300-\u036f]/g, "") // Remove diacritical marks
    .replace(/[^\w\s-]/g, "") // Keep only alphanumeric, spaces, hyphens
    .replace(/\s+/g, "-") // Replace multiple spaces with one hyphen
    .replace(/--+/g, "-") // Replace multiple hyphens with one hyphen
    .replace(/^-+|-+$/g, ""); // Remove leading/trailing hyphens

  // URL encode the slug using encodeURIComponent
  slug = encodeURIComponent(slug);

  // Replace %20 (encoded space) back to -
  slug = slug.replace(/%20/g, "-");

  return slug;
};

/**
 * Formats a comma-separated string into a Python-style list (string array) with single quotes.
 *
 * @param str The comma-separated string to format.
 * @returns A string representing a Python-style list of strings.  Returns "[]" for empty input.
 *
 * @example
 *   formatStringArray("INV 1,INV 2")  =>  "['INV 1', 'INV 2']"
 *   formatStringArray("")             =>  "[]"
 *   formatStringArray("production")    =>  "['production']"
 */
export function formatStringArray(str: string) {
  // Handle empty or null input string
  if (!str) {
    return "[]"; // Return an empty Python-style list
  }

  // Split the string by commas, trim whitespace, and filter out empty strings
  const arr = str
    .split(",") // Split the string into an array
    .map((s) => s.trim()) // Remove leading/trailing whitespace from each element
    .filter((s) => s !== ""); // Remove any empty strings

  // Format each element with single quotes
  const formattedArr = arr.map((s) => `'${s}'`); // Add single quotes around each string

  // Join the formatted elements with commas and enclose in square brackets
  return `[${formattedArr.join(", ")}]`; // Create the final Python-style list string
}
